import { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDateInput, parseDateValue } from '@/lib/dates';
import { cn } from '@/lib/utils';

type DateInputProps = {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

const toDisplayDate = (date: Date) => {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export function DateInput({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder = 'dd/mm/yyyy',
  disabled,
  className,
}: DateInputProps) {
  const [open, setOpen] = useState(false);
  const selectedDate = parseDateValue(value) ?? undefined;

  return (
    <div
      className={cn(
        'flex h-10 box-border overflow-hidden rounded-sm border border-gray-200 bg-white transition-colors focus-within:border-[#4369a0] focus-within:ring-1 focus-within:ring-[#4369a0]/25',
        disabled && 'bg-gray-50',
        className
      )}
    >
      <input
        id={id}
        name={name}
        inputMode="numeric"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(formatDateInput(e.target.value))}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={10}
        className="h-full min-h-0 w-full bg-transparent px-3 text-sm text-gray-900 outline-none disabled:cursor-not-allowed disabled:text-gray-600"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            className="h-full w-10 shrink-0 rounded-none border-l border-gray-200 text-gray-500 hover:bg-[#4369a0]/10 hover:text-[#4369a0]"
            aria-label="Open date picker"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="z-[100] w-auto p-0" sideOffset={6}>
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate}
            onSelect={(date) => {
              if (!date) return;
              onChange(toDisplayDate(date));
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
